import axios from "axios";
import { LIST_JOB_ADVERTS_URL } from "@/utils/constants/constants.js";
import { validate_non_sub_zero_number } from "@/utils/validations/validate_inputs";

function createEmptyAdvertApplicantCounts() {
  return {
    byId: Object.create(null),
    byTitle: Object.create(null),
    byReference: Object.create(null),
  };
}

function buildAdvertDedupTitle(advert) {
  return advert?.title || advert?.advert_title || "advert";
}

function buildAdvertDedupReference(advert, index) {
  return advert?.reference_number || advert?.ref_number || index;
}

function buildAdvertDedupFallback(advert, index) {
  return `${buildAdvertDedupTitle(advert)}-${buildAdvertDedupReference(advert, index)}`;
}

export default {
  methods: {
    create_empty_advert_applicant_counts: createEmptyAdvertApplicantCounts,
    get_hydrated_advert_entity_type_ids() {
      return new Set(
        Array.isArray(this.advert_count_hydrated_entity_type_ids)
          ? this.advert_count_hydrated_entity_type_ids.map(Number).filter((value) => Number.isFinite(value) && value > 0)
          : []
      );
    },
    set_hydrated_advert_entity_type_ids(entity_type_ids = []) {
      const next_ids = Array.from(
        new Set(
          entity_type_ids
            .map(Number)
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      this.advert_count_hydrated_entity_type_ids = next_ids;
    },
    can_increment_advert_applicant_count(store, key) {
      return !!store && key !== null && typeof key !== "undefined" && key !== "";
    },
    increment_advert_applicant_count(store, key) {
      if (!this.can_increment_advert_applicant_count(store, key)) return;
      const current_value = Number(Reflect.get(store, key)) || 0;
      Reflect.set(store, key, current_value + 1);
    },
    extract_nested_job_advert_results(payload) {
      return Array.isArray(payload?.data?.results) ? payload.data.results : [];
    },
    extract_job_advert_response_list(payload) {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.results)) return payload.results;
      return this.extract_nested_job_advert_results(payload);
    },
    get_advert_timestamp_candidates(advert) {
      return [
        advert?.created_at,
        advert?.date_created,
        advert?.published_date,
        advert?.opening_date,
        advert?.open_date,
        advert?.updated_at,
        advert?.modified_at,
      ];
    },
    parse_advert_timestamp(candidate) {
      if (!candidate) return null;
      const timestamp = new Date(candidate).getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    },
    get_advert_fallback_timestamp(advert) {
      const fallback_id = Number(advert?.id);
      return Number.isFinite(fallback_id) ? fallback_id : 0;
    },
    normalize_advert_timestamp(advert) {
      for (const candidate of this.get_advert_timestamp_candidates(advert)) {
        const timestamp = this.parse_advert_timestamp(candidate);
        if (timestamp !== null) return timestamp;
      }
      return this.get_advert_fallback_timestamp(advert);
    },
    get_advert_entity_type_id_candidates(advert) {
      return [
        advert?.entity_type_id,
        advert?.type_id,
        advert?.job_profile_id,
        advert?.entity_type?.id,
        advert?.job_profile?.id,
      ];
    },
    normalize_entity_type_candidate(candidate) {
      const value = Number(candidate || 0);
      return Number.isFinite(value) && value > 0 ? value : null;
    },
    get_advert_entity_type_id(advert) {
      for (const candidate of this.get_advert_entity_type_id_candidates(advert)) {
        const normalized = this.normalize_entity_type_candidate(candidate);
        if (normalized !== null) return normalized;
      }
      return null;
    },
    get_profile_display_name(profile) {
      const candidates = [
        profile?.entity_type_name,
        profile?.entity_type,
        profile?.job_profile,
        profile?.profile_name,
        profile?.name,
      ];
      return candidates.find((value) => String(value || "").trim()) || "";
    },
    build_project_advert_dedup_key(advert, index) {
      return String(advert?.id || advert?.advert_id || buildAdvertDedupFallback(advert, index));
    },
    append_unique_project_advert(unique_adverts, seen, advert, index) {
      const key = this.build_project_advert_dedup_key(advert, index);
      if (seen.has(key)) return;
      seen.add(key);
      unique_adverts.push(advert);
    },
    async fetch_all_project_job_adverts(project_id) {
      const adverts_response = await axios.get(LIST_JOB_ADVERTS_URL, {
        headers: this.headers,
        params: {
          project_id,
          all_adverts: true,
        },
      });

      if (adverts_response?.data?.status !== "success") {
        return [];
      }

      const adverts = this.extract_job_advert_response_list(adverts_response.data);

      const unique_adverts = [];
      const seen = new Set();

      adverts.forEach((advert, index) => {
        this.append_unique_project_advert(unique_adverts, seen, advert, index);
      });

      return unique_adverts;
    },
    async get_job_adverts() {
      this.job_adverts = [];
      this.loading_job_adverts = true;
      this.advert_applicant_counts = this.create_empty_advert_applicant_counts();
      this.advert_count_hydrated_entity_type_ids = [];

      const should_list_all_adverts = this.$route?.query?.all_adverts === "true";
      const entity_type_id = parseInt(this.entity_type_id, 10);
      const project_id = parseInt(this.project_id, 10);

      if (
        (should_list_all_adverts && !validate_non_sub_zero_number(project_id)) ||
        (!should_list_all_adverts && !validate_non_sub_zero_number(entity_type_id))
      ) {
        this.loading_job_adverts = false;
        return;
      }

      try {
        let adverts = [];

        if (should_list_all_adverts) {
          adverts = await this.fetch_all_project_job_adverts(project_id);
        } else {
          const adverts_response = await axios.get(LIST_JOB_ADVERTS_URL, {
            headers: this.headers,
            params: { entity_type_id },
          });

          if (adverts_response?.data?.status !== "success") {
            this.show_error(adverts_response?.data?.message || "Failed to fetch job adverts");
            this.loading_job_adverts = false;
            return;
          }

          adverts = this.extract_job_advert_response_list(adverts_response.data);
        }

        this.job_adverts = adverts.sort(
          (left, right) => this.normalize_advert_timestamp(right) - this.normalize_advert_timestamp(left)
        );
        this.loading_job_adverts = false;
      } catch (error) {
        this.show_error(error?.response?.data?.message || "Error fetching job adverts");
      } finally {
        if (this.loading_job_adverts) {
          this.loading_job_adverts = false;
        }
      }
    },
    open_create_advert_modal() {
      this.selected_advert = null;
      this.edit_advert_data = {
        id: null,
        title: "",
        reference_number: "",
        published_date: "",
        closing_date: "",
        vacancies: null,
        details: "",
        sections: [],
      };
      this.selected_row = {
        id: Number(this.selectedJobId || this.entity_type_id),
        entity_type: this.entity_type_name || "",
      };
      this.create_advert_open_nonce += 1;
    },
    open_view_advert_modal(advert) {
      this.selected_advert = advert ? { ...advert } : null;
      this.view_advert_open_nonce += 1;
    },
    open_edit_advert_modal(advert) {
      this.selected_advert = advert ? { ...advert } : null;
      this.edit_advert_open_nonce += 1;
    },
    open_edit_advert_modal_from_view(advert) {
      this.open_edit_advert_modal(advert);
      window.setTimeout(() => {
        const edit_modal = document.getElementById("edit_advert_modal");
        const bootstrap_modal = window.bootstrap?.Modal;
        if (!edit_modal || !bootstrap_modal) return;
        bootstrap_modal.getOrCreateInstance(edit_modal).show();
      }, 180);
    },
    handle_view_advert_applicants(advert) {
      this.redirect_to_applicants(advert?.entity_type_id, advert?.id);
    },
    async on_advert_created() {
      this.show_success("Advert created successfully.");
      await this.get_job_adverts();
    },
    async on_advert_updated() {
      this.show_success("Advert updated successfully.");
      await this.get_job_adverts();
    },
    on_advert_error(message) {
      this.show_error(message || "Advert action failed.");
    },
    slugify_advert_title(value) {
      const slug = String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      return slug || "job-advert";
    },
    build_advert_share_link(advert) {
      const public_token = String(advert?.public_token || "").trim();

      if (!public_token) {
        return "";
      }

      const advert_title =
        advert?.advert_title ||
        advert?.title ||
        advert?.name ||
        advert?.job_title ||
        advert?.advert_name ||
        "job-advert";
      const url = new URL(`/jobs/${this.slugify_advert_title(advert_title)}`, window.location.origin);
      url.searchParams.set("ref", public_token);
      return url.toString();
    },
    async copy_advert_link(advert) {
      const link = this.build_advert_share_link(advert);

      if (!link) {
        this.show_error("Unable to copy advert link: missing advert details.");
        return;
      }

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(link);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = link;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        this.show_success("Advert link copied.");
      } catch (error) {
        this.show_error("Unable to copy advert link. Please try again.");
      }
    },
    redirect_to_applicants(entity_type_input, advert_id = null) {
      const resolved_id = Number(
        entity_type_input?.entity_type_id ?? entity_type_input ?? this.entity_type_id
      );

      if (!Number.isFinite(resolved_id) || resolved_id <= 0) {
        this.show_error("Unable to open applicants: missing entity type.");
        return;
      }

      sessionStorage.setItem("entity_type_id", String(resolved_id));
      sessionStorage.setItem("entity_type_name", this.entity_type_name || "");
      if (advert_id) {
        sessionStorage.setItem("advert_id", String(advert_id));
      } else {
        sessionStorage.removeItem("advert_id");
      }

      this.$router.push({
        path: "/view_applicants",
        query: {
          entity_type_id: String(resolved_id),
          entity_type_name: this.entity_type_name,
          ...(advert_id ? { advert_id: String(advert_id) } : {}),
        },
      });
    },
  },
};
